// client/src/coms/views/ChatView.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useMyPermissions } from '../usePermissions';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { comsApiGet, comsApiPost } from '../comsApi';
import ChatViewScrollbarStyle from './chatview_parts/ChatViewScrollbarStyle';
import ChatMessageList from './chatview_parts/ChatMessageList';
import ChatComposer from './chatview_parts/ChatComposer';
import ChatLiveStatus from './chatview_parts/ChatLiveStatus';
import ReportDialog from '../components/ReportDialog';
import { captureScroll, readTabMemory, restoreScroll, writeTabMemory } from '../workspace/tabMemory';
import { markEngaged } from '../workspace/engagement';
import { usePersonalisation } from '../personalisation/PersonalisationProvider';
import { beginActivity } from '../accounts/accountsApi';
import WhatShallWePlayModal from './chatview_parts/WhatShallWePlayModal';
import useChatEmojiPopoverClose from './chatview_parts/useChatEmojiPopoverClose';
import useChatMembers from './chatview_parts/useChatMembers';
import useChatMessages from './chatview_parts/useChatMessages';
import useChannelE2ee from '../e2ee/useChannelE2ee';
import ChannelEncryptionBar from '../e2ee/ChannelEncryptionBar';
import EncryptedReportDialog from '../e2ee/EncryptedReportDialog';
import { decryptedResult } from '../e2ee/cryptoSession';
import useChatModerationActions from './chatview_parts/useChatModerationActions';
import useChatDerivedMessages from './chatview_parts/useChatDerivedMessages';
import { buildSharedMessageActions } from './chatview_parts/messageActions';
import { openInteractionCreator } from '../interactions/interactionStore';
import { useChannelState, ArchivedChannelBar } from '../organisation/channelLifecycleUi';
import { saveBookmark } from '../organisation/bookmarkActions';
import {
  safeObj,
  toInt,
  extractNumericId,
  normalizeUser,
  getUserId,
  getUserLabel,
} from './chatview_parts/chatUtils';
import {
  composeOutgoingMessageBodyWithUploads,
  uploadFilesToComms,
} from './chatview_parts/chatUploadUtils';

function isMobileViewport() {
  try {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return !!window.matchMedia('(max-width: 900px)').matches;
  } catch {
    return false;
  }
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

const MESSAGE_FONT_OPTIONS = [
  { value: 'inherit', label: 'Default' },
  { value: 'Arial', label: 'Arial' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Courier New', label: 'Courier New' },
  { value: 'Comic Sans MS', label: 'Comic Sans MS' },
];

function messageFontStorageKey() {
  return 'vox:chat:lastMessageFont';
}

function normalizeMessageFont(value) {
  const raw = String(value || '').trim();
  if (!raw || raw === 'inherit') return 'inherit';
  return MESSAGE_FONT_OPTIONS.some((opt) => opt.value === raw) ? raw : 'inherit';
}

function readMessageFontFromStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return 'inherit';
    return normalizeMessageFont(window.localStorage.getItem(messageFontStorageKey()) || 'inherit');
  } catch {
    return 'inherit';
  }
}

function writeMessageFontToStorage(value) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(messageFontStorageKey(), normalizeMessageFont(value));
  } catch {}
}

function normalizeQuoteMessage(msg) {
  const m = msg && typeof msg === 'object' ? msg : {};
  const author = String(m.author || '').trim();
  const stamp = String(m.stamp || '').trim();
  const header = author && stamp ? `${author} • ${stamp}` : author || stamp || 'Message';
  const text = String(m.text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  return { author, stamp, header, text };
}

function buildQuoteBlockFromPending(pq) {
  const q = pq && typeof pq === 'object' ? pq : null;
  if (!q) return '';
  const header = String(q.header || '').trim() || 'Message';
  const rawText = String(q.text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = rawText.split('\n');
  const quotedLines = lines.map((l) => `> ${l}`.replace(/^> $/, '>')).join('\n');
  return `> ${header}\n${quotedLines}\n\n`;
}

function escapeRegex(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function normalizeMentionToken(value) {
  return String(value || '').trim().toLowerCase();
}

function extractBodyMentionInfo(body) {
  const text = String(body || '');
  const ids = new Set();
  const names = new Set();
  let everyone = false;
  let m;

  const idMentionRe = /<@(\d+)>/g;
  while ((m = idMentionRe.exec(text))) {
    const id = toInt(m[1], 0);
    if (id > 0) ids.add(id);
  }

  const pipeMentionRe = /@\|([^|\r\n]+)\|/g;
  while ((m = pipeMentionRe.exec(text))) {
    const raw = normalizeMentionToken(m[1]);
    if (raw && raw !== 'everyone') names.add(raw);
  }

  const bracketMentionRe = /@\[([^\]\r\n]+)\]/g;
  while ((m = bracketMentionRe.exec(text))) {
    const raw = normalizeMentionToken(m[1]);
    if (raw && raw !== 'everyone') names.add(raw);
  }

  const plainMentionRe = /(^|[^A-Za-z0-9_])@([A-Za-z0-9][A-Za-z0-9_.-]{0,63})\b/g;
  while ((m = plainMentionRe.exec(text))) {
    const raw = normalizeMentionToken(m[2]);
    if (!raw) continue;
    if (raw === 'everyone') {
      everyone = true;
      continue;
    }
    names.add(raw);
  }

  if (/(^|[\s.,!?()[\]{}:;"'`~\-_\/\\])@everyone\b/i.test(text)) {
    everyone = true;
  }

  return { ids, names, everyone };
}

function bodyMentionsEveryone(body) {
  return !!extractBodyMentionInfo(body).everyone;
}

function buildMentionNameVariants(values) {
  const out = [];
  const seen = new Set();

  safeArray(values).forEach((value) => {
    const raw = String(value || '').trim();
    if (!raw) return;

    const variants = [raw];
    const bracketMatch = raw.match(/^@?\[(.+)\]$/);
    if (bracketMatch && bracketMatch[1]) variants.push(bracketMatch[1]);
    if (raw.startsWith('@')) variants.push(raw.slice(1));

    variants.forEach((variant) => {
      const v = String(variant || '').trim();
      const k = v.toLowerCase();
      if (!k || seen.has(k)) return;
      seen.add(k);
      out.push(v);
    });
  });

  return out;
}

function bodyMentionsUser(body, userId, mentionNames) {
  const info = extractBodyMentionInfo(body);
  const uid = toInt(userId, 0);
  if (uid > 0 && info.ids.has(uid)) return true;

  const names = buildMentionNameVariants(mentionNames)
    .map((name) => normalizeMentionToken(name))
    .filter(Boolean);

  for (let i = 0; i < names.length; i += 1) {
    if (info.names.has(names[i])) return true;
  }

  const text = String(body || '');
  for (let i = 0; i < names.length; i += 1) {
    const name = String(names[i] || '').trim();
    if (!name) continue;
    const escaped = escapeRegex(name);
    if (new RegExp(`@\\[${escaped}\\]`, 'i').test(text)) return true;
    if (new RegExp(`(^|[^A-Za-z0-9_])@${escaped}(?=$|[^A-Za-z0-9_])`, 'i').test(text)) return true;
    if (new RegExp(`@\\|${escaped}\\|`, 'i').test(text)) return true;
  }

  return false;
}

function getUnreadMentionMatch(msg, myUserId, mentionNames, lastSeenMessageId) {
  const m = msg && typeof msg === 'object' ? msg : {};
  const id = toInt(m.id, 0);
  const authorId = toInt(m.user_id, 0);
  const seenId = toInt(lastSeenMessageId, 0);
  if (!id || id <= seenId) return null;
  if (authorId > 0 && authorId === toInt(myUserId, 0)) return null;

  const body = String(m.body || m.text || '');
  const mentionsEveryone = bodyMentionsEveryone(body);
  const mentionsMe = bodyMentionsUser(body, myUserId, mentionNames);
  if (!mentionsEveryone && !mentionsMe) return null;

  return {
    messageId: id,
    mentionsEveryone,
    mentionsMe,
    body,
    authorId,
  };
}

function messageMatchesUnreadMention(msg, myUserId, mentionNames, lastSeenMessageId) {
  return !!getUnreadMentionMatch(msg, myUserId, mentionNames, lastSeenMessageId);
}

function findUnreadMentionMessageId(messages, myUserId, mentionNames, readState) {
  const list = safeArray(messages);
  const state = readState && typeof readState === 'object' ? readState : {};
  const lastSeenMessageId = toInt(state.last_seen_message_id, 0);
  const lastMentionMessageId = toInt(state.last_mention_message_id, 0);

  if (lastMentionMessageId > lastSeenMessageId) {
    const direct = list.find((msg) => toInt(msg && msg.id, 0) === lastMentionMessageId);
    if (direct && messageMatchesUnreadMention(direct, myUserId, mentionNames, lastSeenMessageId)) {
      return lastMentionMessageId;
    }
  }

  for (let i = list.length - 1; i >= 0; i -= 1) {
    const msg = list[i];
    if (messageMatchesUnreadMention(msg, myUserId, mentionNames, lastSeenMessageId)) {
      return toInt(msg && msg.id, 0);
    }
  }

  return 0;
}

function flashMessageElement(el) {
  if (!el) return;

  try {
    if (el.__voxMentionFlashTimerA) clearTimeout(el.__voxMentionFlashTimerA);
    if (el.__voxMentionFlashTimerB) clearTimeout(el.__voxMentionFlashTimerB);
  } catch {}

  const prevTransition = el.style.transition || '';
  const prevBoxShadow = el.style.boxShadow || '';
  const prevBackground = el.style.background || '';
  const prevBorderRadius = el.style.borderRadius || '';

  try {
    el.style.transition = 'background 220ms ease, box-shadow 220ms ease';
    el.style.borderRadius = prevBorderRadius || '10px';
    el.style.background = 'rgba(110,160,255,0.12)';
    el.style.boxShadow = '0 0 0 1px rgba(110,160,255,0.45), 0 0 0 6px rgba(110,160,255,0.12)';
  } catch {}

  try {
    el.__voxMentionFlashTimerA = setTimeout(() => {
      try {
        el.style.background = 'rgba(110,160,255,0.06)';
        el.style.boxShadow = '0 0 0 1px rgba(110,160,255,0.18), 0 0 0 2px rgba(110,160,255,0.08)';
      } catch {}
    }, 700);

    el.__voxMentionFlashTimerB = setTimeout(() => {
      try {
        el.style.transition = prevTransition;
        el.style.boxShadow = prevBoxShadow;
        el.style.background = prevBackground;
        el.style.borderRadius = prevBorderRadius;
      } catch {}
    }, 2200);
  } catch {}
}

function tryFindMessageElement(messageId) {
  const id = toInt(messageId, 0);
  if (!id || typeof document === 'undefined') return null;

  const selectors = [
    `[data-message-id="${id}"]`,
    `[data-msg-id="${id}"]`,
    `[data-id="${id}"]`,
    `#coms-message-${id}`,
    `#message-${id}`,
  ];

  for (const selector of selectors) {
    try {
      const el = document.querySelector(selector);
      if (el) return el;
    } catch {}
  }

  return null;
}

function mergeMessagesById(existingRows, incomingRows) {
  const map = new Map();

  safeArray(existingRows).forEach((row) => {
    const id = toInt(row && row.id, 0);
    if (!id) return;
    map.set(String(id), row);
  });

  safeArray(incomingRows).forEach((row) => {
    const id = toInt(row && row.id, 0);
    if (!id) return;
    map.set(String(id), row);
  });

  return Array.from(map.values()).sort((a, b) => toInt(a && a.id, 0) - toInt(b && b.id, 0));
}

function normalizeReactionEntry(entry) {
  const e = entry && typeof entry === 'object' ? entry : {};
  const users = safeArray(e.users).map((u) => {
    const user = u && typeof u === 'object' ? u : {};
    return {
      id: toInt(user.id, 0),
      username: String(user.username || '').trim(),
    };
  });

  return {
    emoji: String(e.emoji || '').trim(),
    count: Math.max(0, toInt(e.count, 0)),
    reacted: !!e.reacted,
    users,
  };
}

function normalizeReactionsArray(list) {
  return safeArray(list)
    .map((entry) => normalizeReactionEntry(entry))
    .filter((entry) => entry.emoji);
}

function isScrolledNearBottom(el, threshold = 72) {
  if (!el) return false;
  const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
  return remaining <= threshold;
}

function scrollToBottomHard(el) {
  if (!el) return;
  const prevBehavior = el.style.scrollBehavior || '';

  try {
    el.style.scrollBehavior = 'auto';
    el.scrollTop = el.scrollHeight;
  } catch {}

  try {
    requestAnimationFrame(() => {
      try {
        el.style.scrollBehavior = 'auto';
        el.scrollTop = el.scrollHeight;
      } catch {}
      requestAnimationFrame(() => {
        try {
          el.style.scrollBehavior = prevBehavior;
        } catch {}
      });
    });
  } catch {
    try {
      el.style.scrollBehavior = prevBehavior;
    } catch {}
  }
}

export default function ChatView({
  server,
  channel,
  user,
  messageSearch = '',
  messageChannelKind = 'text',
  messageTtlMinutes = 0,
  onMessageMetaChanged,
  onMessageRemoved,
  // Tabs and split view: where this conversation's scroll position and pending reply are remembered,
  // and whether this pane may take keyboard focus when it opens.
  memoryKey = '',
  autoFocus = true,
}) {
  const { themeBase } = usePersonalisation();
  const [isShellMobile, setIsShellMobile] = useState(() => isMobileViewport());
  const [pendingUploads, setPendingUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [initialReadState, setInitialReadState] = useState(null);
  const [mentionSnapshotReady, setMentionSnapshotReady] = useState(false);
  const [mentionBannerDismissed, setMentionBannerDismissed] = useState(false);
  const [mentionJumpBusy, setMentionJumpBusy] = useState(false);
  const [reactionPickerMessage, setReactionPickerMessage] = useState(null);
  const [reactionsByMessageId, setReactionsByMessageId] = useState({});
  const [myRoles, setMyRoles] = useState([]);
  const [pinBusyMessageId, setPinBusyMessageId] = useState('');
  const [superPinBusyMessageId, setSuperPinBusyMessageId] = useState('');
  const [whatShallWePlayModalOpen, setWhatShallWePlayModalOpen] = useState(false);
  const [whatShallWePlayPayload, setWhatShallWePlayPayload] = useState(null);
  const [whatShallWePlayNotice, setWhatShallWePlayNotice] = useState('');

  const serverKey = server && server.id != null ? String(server.id) : '';
  const channelUiId = channel && channel.id != null ? String(channel.id) : '';
  const channelDbId = extractNumericId(channelUiId);
  const me = useMemo(() => normalizeUser(user), [user]);
  const myUserId = useMemo(() => getUserId(me), [me]);
  const isLoggedIn = useMemo(() => getUserId(me) != null || !!getUserLabel(me), [me]);

  const ownerUserId = useMemo(() => {
    const s = safeObj(server);
    const id =
      s.owner_user_id != null
        ? s.owner_user_id
        : s.ownerId != null
          ? s.ownerId
          : s.owner_userId != null
            ? s.owner_userId
            : s.owner_id != null
              ? s.owner_id
              : null;
    return toInt(id, 0);
  }, [server]);

  const activeChannelKind =
    String(messageChannelKind || '').trim().toLowerCase() === 'voice' ? 'voice' : 'text';
  const channelKind = String(channel && channel.kind ? channel.kind : '').trim().toLowerCase();
  const isMessageChannel = !!channel && channelKind === activeChannelKind;
  // Effective permissions in this channel (roles + channel overrides), refreshed when they change.
  const permChannelId = toInt(channel && (channel.db_id != null ? channel.db_id : channel.id), 0);
  const chatPerms = useMyPermissions(serverKey, activeChannelKind, permChannelId);
  const canSendHere = chatPerms.has('send_messages');
  const canType = isLoggedIn && isMessageChannel && canSendHere;

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const memoryUserId = useMemo(() => getUserId(normalizeUser(user)), [user]);
  const [pendingQuote, setPendingQuote] = useState(() => {
    const saved = readTabMemory(memoryUserId, memoryKey);
    return saved && saved.quote ? saved.quote : null;
  });
  const [selectedMessageFont, setSelectedMessageFont] = useState(() =>
    readMessageFontFromStorage()
  );

  const openWhatShallWePlayModal = useCallback((payload) => {
    setWhatShallWePlayPayload(payload && typeof payload === 'object' ? payload : null);
    setWhatShallWePlayModalOpen(true);
  }, []);

  const closeWhatShallWePlayModal = useCallback(() => {
    setWhatShallWePlayModalOpen(false);
    setWhatShallWePlayPayload(null);
  }, []);

  const handleWhatShallWePlaySubmitted = useCallback((info) => {
    const message = String((info && info.message) || 'You submitted your votes.').trim();
    setWhatShallWePlayNotice(message || 'You submitted your votes.');
  }, []);

  const scrollerRef = useRef(null);
  const messagesContentRef = useRef(null);
  const inputRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const emojiPopoverRef = useRef(null);
  const reactionPickerRef = useRef(null);
  const markReadBusyRef = useRef(false);
  const lastMarkedMessageIdRef = useRef(0);
  const shouldPinToBottomRef = useRef(true);
  const lastBottomReactionSignatureRef = useRef('');

  // Reopening a tab puts the list back where the user left it (once per conversation visit).
  const restoreScrollRef = useRef(null);
  const restoredForRef = useRef('');
  if (memoryKey && restoredForRef.current !== memoryKey) {
    restoredForRef.current = memoryKey;
    const saved = readTabMemory(memoryUserId, memoryKey);
    restoreScrollRef.current =
      saved && saved.scroll && !saved.scroll.atBottom
        ? () => {
            restoreScrollRef.current = null;
            const el = scrollerRef.current;
            const ok = restoreScroll(el, saved.scroll);
            if (!ok) return false;
            shouldPinToBottomRef.current = false;
            // Late "stick to bottom" passes from the initial load can still run for a few frames; hold the
            // restored place until then unless the user starts scrolling.
            let userMoved = false;
            const stop = () => {
              userMoved = true;
            };
            if (el) {
              el.addEventListener('wheel', stop, { passive: true, once: true });
              el.addEventListener('touchstart', stop, { passive: true, once: true });
              el.addEventListener('keydown', stop, { once: true });
            }
            const reapply = () => {
              if (userMoved || !scrollerRef.current) return;
              shouldPinToBottomRef.current = false;
              restoreScroll(scrollerRef.current, saved.scroll);
            };
            requestAnimationFrame(() => requestAnimationFrame(reapply));
            [60, 150, 350].forEach((ms) => setTimeout(reapply, ms));
            setTimeout(() => {
              if (!el) return;
              el.removeEventListener('wheel', stop);
              el.removeEventListener('touchstart', stop);
              el.removeEventListener('keydown', stop);
            }, 400);
            return true;
          }
        : null;
  }

  useEffect(() => {
    if (!whatShallWePlayNotice) return undefined;
    const t = setTimeout(() => {
      setWhatShallWePlayNotice('');
    }, 3000);
    return () => clearTimeout(t);
  }, [whatShallWePlayNotice]);

  useEffect(() => {
    function onResize() {
      setIsShellMobile(isMobileViewport());
    }

    onResize();

    if (typeof window === 'undefined') return undefined;
    const vv = window.visualViewport || null;

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    if (vv) {
      vv.addEventListener('resize', onResize);
      vv.addEventListener('scroll', onResize);
    }

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (vv) {
        vv.removeEventListener('resize', onResize);
        vv.removeEventListener('scroll', onResize);
      }
    };
  }, []);

  useEffect(() => {
    markReadBusyRef.current = false;
    lastMarkedMessageIdRef.current = 0;
    shouldPinToBottomRef.current = true;
    lastBottomReactionSignatureRef.current = '';
  }, [serverKey, channelUiId, activeChannelKind]);

  // Remember the scroll position while the user reads (throttled to one capture per frame).
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !memoryKey) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        writeTabMemory(memoryUserId, memoryKey, { scroll: captureScroll(el) });
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [memoryKey, memoryUserId]);

  // The pending reply belongs to the conversation it was started in; it is only saved for that one (the
  // view stays mounted when the conversation changes, and is then given that conversation's own reply).
  const quoteOwnerRef = useRef(memoryKey);
  useEffect(() => {
    if (memoryKey && !quoteOwnerRef.current) quoteOwnerRef.current = memoryKey;
    if (memoryKey && quoteOwnerRef.current === memoryKey) writeTabMemory(memoryUserId, memoryKey, { quote: pendingQuote || null });
  }, [memoryKey, memoryUserId, pendingQuote]);

  useEffect(() => {
    setSelectedMessageFont(readMessageFontFromStorage());
  }, []);

  useEffect(() => {
    writeMessageFontToStorage(selectedMessageFont);
  }, [selectedMessageFont]);

  useChatEmojiPopoverClose({
    showEmojiPicker,
    setShowEmojiPicker,
    emojiButtonRef,
    emojiPopoverRef,
  });

  useEffect(() => {
    if (!reactionPickerMessage) return undefined;
    if (typeof document === 'undefined') return undefined;

    function onPointerDown(e) {
      const target = e.target;
      if (reactionPickerRef.current && reactionPickerRef.current.contains(target)) return;
      setReactionPickerMessage(null);
    }

    function onKeyDown(e) {
      if (String(e.key || '') !== 'Escape') return;
      setReactionPickerMessage(null);
    }

    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [reactionPickerMessage]);

  const { membersNameMap, membersLoaded, myChatLabel } = useChatMembers({
    serverKey,
    isLoggedIn,
    myUserId,
    me,
  });

  const mentionMembers = useMemo(() => {
    const map = membersNameMap && typeof membersNameMap === 'object' ? membersNameMap : {};
    const out = [];
    const seen = new Set();

    Object.keys(map).forEach((key) => {
      const rawName = String(map[key] || '').trim();
      const numericId = toInt(key, 0);
      if (!rawName || !numericId) return;

      const lowered = rawName.toLowerCase();
      if (seen.has(lowered)) return;
      seen.add(lowered);

      out.push({
        user_id: numericId,
        username: rawName,
        display_name: rawName,
        name: rawName,
      });
    });

    if (myUserId > 0 && myChatLabel) {
      const mine = String(myChatLabel || '').trim();
      const lowered = mine.toLowerCase();
      if (mine && !seen.has(lowered)) {
        out.unshift({
          user_id: myUserId,
          username: mine,
          display_name: mine,
          name: mine,
        });
      }
    }

    return out.sort((a, b) => {
      const an = String((a && (a.display_name || a.username || a.name)) || '').toLowerCase();
      const bn = String((b && (b.display_name || b.username || b.name)) || '').toLowerCase();
      if (an < bn) return -1;
      if (an > bn) return 1;
      return 0;
    });
  }, [membersNameMap, myUserId, myChatLabel]);

  const myMentionNames = useMemo(() => {
    const out = [];
    const seen = new Set();

    function add(value) {
      const raw = String(value || '').trim();
      if (!raw) return;
      const key = raw.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(raw);
    }

    add(myChatLabel);
    add(getUserLabel(me));

    mentionMembers.forEach((member) => {
      const id = toInt(member && member.user_id, 0);
      if (id !== toInt(myUserId, 0)) return;
      add(member && member.display_name);
      add(member && member.username);
      add(member && member.name);
    });

    return out;
  }, [myChatLabel, me, mentionMembers, myUserId]);

  useEffect(() => {
    let cancelled = false;

    async function loadMyRoles() {
      const sid = String(serverKey || '').trim();
      const uid = toInt(myUserId, 0);
      if (!sid || !uid) {
        if (!cancelled) setMyRoles([]);
        return;
      }

      try {
        const res = await comsApiGet(
          `/servers/${encodeURIComponent(sid)}/members/${encodeURIComponent(String(uid))}/roles`
        ).catch(() => null);
        if (cancelled) return;
        setMyRoles(safeArray(res && res.roles));
      } catch {
        if (cancelled) return;
        setMyRoles([]);
      }
    }

    loadMyRoles().catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [serverKey, myUserId]);

  const myPermissionsMap = useMemo(() => {
    const out = {};
    // Effective permissions in this channel (server-resolved, channel overrides applied) when loaded.
    if (chatPerms.loaded) {
      for (const key of ['manage_messages', 'edit_any_message', 'delete_any_message', 'delete_own_messages', 'pin_messages', 'add_reactions']) {
        if (chatPerms.has(key)) out[key] = 1;
      }
      out.__resolved = 1;
      return out;
    }

    safeArray(myRoles).forEach((role) => {
      const roleObj = safeObj(role);
      const permissionsMap = safeObj(roleObj.permissions_map);

      Object.keys(permissionsMap).forEach((key) => {
        if (toInt(permissionsMap[key], 0) === 1) out[key] = 1;
      });

      safeArray(roleObj.permissions).forEach((perm) => {
        const key = String(perm && perm.key ? perm.key : '').trim();
        if (!key) return;
        if (toInt(perm && perm.allow, 0) === 1) out[key] = 1;
      });
    });

    return out;
  }, [myRoles, chatPerms]);

  useEffect(() => {
    let cancelled = false;

    setInitialReadState(null);
    setMentionSnapshotReady(false);
    setMentionBannerDismissed(false);
    setMentionJumpBusy(false);

    if (!isLoggedIn || activeChannelKind !== 'text' || !isMessageChannel || !channelDbId) {
      setMentionSnapshotReady(true);
      return undefined;
    }

    (async () => {
      try {
        const res = await comsApiGet(`/text-channels/${channelDbId}/read-state`);
        if (cancelled) return;
        setInitialReadState(res && res.state && typeof res.state === 'object' ? res.state : null);
      } catch {
        if (cancelled) return;
        setInitialReadState(null);
      } finally {
        if (!cancelled) setMentionSnapshotReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, activeChannelKind, isMessageChannel, channelDbId]);

  // Reaction events arrive on the message stream; the handler is defined further down.
  const reactionUpdateHandlerRef = useRef(null);
  const [reportTarget, setReportTarget] = useState(null);
  const [e2eeReportId, setE2eeReportId] = useState(null);
  const onStreamReactionUpdate = useCallback((ev) => {
    if (typeof reactionUpdateHandlerRef.current === 'function') reactionUpdateHandlerRef.current(ev);
  }, []);

  // Writing here keeps this conversation's tab (a preview tab becomes saved). Voice-channel chat has no tab.
  const engagedConversationId = serverKey && channelUiId && activeChannelKind === 'text' ? `c:${serverKey}:${channelUiId}` : '';
  const engageConversation = useCallback(() => markEngaged(engagedConversationId), [engagedConversationId]);

  const channelE2eeRef = useRef(null);

  const {
    draft,
    setDraft,
    streamStatus,
    typingState,
    sendErr,
    setSendErr,
    notifyTyping,
    messagesRaw,
    setMessagesRaw,
    loading,
    loadErr,
    sending,
    editingMessageId,
    setEditingMessageId,
    editDraft,
    setEditDraft,
    editBusyMessageId,
    setEditBusyMessageId,
    deleteBusyMessageId,
    setDeleteBusyMessageId,
    messageActionErr,
    setMessageActionErr,
    hoveredMessageId,
    setHoveredMessageId,
    hideScrollbar,
    setHideScrollbar,
    stickToBottomRef,
    releaseBottomHold,
    maybeAutoScroll,
    send,
    applyIncomingUpdateLike,
    removeMessageById,
  } = useChatMessages({
    e2eeRef: channelE2eeRef,
    serverKey,
    channelUiId,
    channelDbId,
    channelKind: activeChannelKind,
    isMessageChannel,
    isLoggedIn,
    canType,
    inputRef,
    scrollerRef,
    messageTtlMinutes,
    pauseInitialStream:
      activeChannelKind === 'text' && isMessageChannel && !mentionSnapshotReady,
    onIncomingMessageUpdate: onMessageMetaChanged,
    onIncomingMessageDelete: onMessageRemoved,
    onIncomingReactionUpdate: onStreamReactionUpdate,
    myUserId,
    autoFocus,
    restoreScrollRef,
    onEngaged: engageConversation,
  });

  // End-to-end encrypted servers: policy, this device's session for the channel, decryption and the send path.
  const channelE2ee = useChannelE2ee({ serverKey, channelDbId, channelKind: activeChannelKind, messagesRaw });
  channelE2eeRef.current = channelE2ee;

  // Typing pings follow the user's edits, not drafts restored when switching channels.
  const typingChannelRef = useRef(channelUiId);
  useEffect(() => {
    if (typingChannelRef.current !== channelUiId) {
      typingChannelRef.current = channelUiId;
      return;
    }
    notifyTyping(draft);
  }, [draft, channelUiId, notifyTyping]);

  const {
    canModerateMessage,
    canEditMessage,
    canDeleteMessage,
    canPinMessage,
    canSuperPinMessage,
    isMessageDeleted,
    isMessageEdited,
    beginEditMessage,
    cancelEditMessage,
    saveEditMessage,
    deleteMessage,
    togglePinnedMessage,
    toggleSuperPinnedMessage,
  } = useChatModerationActions({
    myUserId,
    ownerUserId,
    myPermissionsMap,
    setEditingMessageId,
    setEditDraft,
    setEditBusyMessageId,
    setDeleteBusyMessageId,
    setPinBusyMessageId,
    setSuperPinBusyMessageId,
    setMessageActionErr,
    setHoveredMessageId,
    editDraft,
    applyIncomingUpdateLike,
    removeMessageById,
    onMessageMetaChanged,
    onMessageRemoved,
  });

  const mergedMessagesRaw = useMemo(() => {
    return safeArray(channelE2ee.displayRows).map((msg) => {
      const base = msg && typeof msg === 'object' ? msg : {};
      const id = toInt(base.id, 0);
      if (!id) return base;

      return {
        ...base,
        reactions: normalizeReactionsArray(reactionsByMessageId[String(id)]),
      };
    });
  }, [channelE2ee.displayRows, reactionsByMessageId]);

  const {
    messages,
    messageSearchNorm,
    filteredMessages,
    messagesWithGroupMeta,
  } = useChatDerivedMessages({
    messagesRaw: mergedMessagesRaw,
    membersNameMap,
    myChatLabel,
    myUserId,
    ownerUserId,
    messageSearch,
    canModerateMessage,
    isMessageEdited,
    isMessageDeleted,
  });

  const latestLoadedMessageId = useMemo(() => {
    let maxId = 0;
    for (const msg of safeArray(messagesRaw)) {
      const id = toInt(msg && msg.id, 0);
      if (id > maxId) maxId = id;
    }
    return maxId;
  }, [messagesRaw]);

  const visibleMessageIds = useMemo(() => {
    return Array.from(
      new Set(
        safeArray(messagesRaw)
          .map((msg) => toInt(msg && msg.id, 0))
          .filter((id) => id > 0)
      )
    );
  }, [messagesRaw]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;

    function updatePinnedState() {
      shouldPinToBottomRef.current = isScrolledNearBottom(el, 72);
    }

    updatePinnedState();
    el.addEventListener('scroll', updatePinnedState, { passive: true });

    return () => {
      el.removeEventListener('scroll', updatePinnedState);
    };
  }, [scrollerRef, channelUiId, serverKey, activeChannelKind]);

  useEffect(() => {
    let cancelled = false;

    if (!isLoggedIn || activeChannelKind !== 'text' || !isMessageChannel || !visibleMessageIds.length) {
      setReactionsByMessageId({});
      return undefined;
    }

    (async () => {
      try {
        const res = await comsApiPost('/messages/reactions/batch', {
          messageIds: visibleMessageIds,
        });
        if (cancelled) return;

        const rawMap =
          res && res.reactionsByMessageId && typeof res.reactionsByMessageId === 'object'
            ? res.reactionsByMessageId
            : {};
        const next = {};
        visibleMessageIds.forEach((id) => {
          next[String(id)] = normalizeReactionsArray(rawMap[String(id)]);
        });
        setReactionsByMessageId(next);
      } catch {
        if (cancelled) return;
        setReactionsByMessageId({});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, activeChannelKind, isMessageChannel, visibleMessageIds]);

  const applyReactionSummary = useCallback((messageId, summary) => {
    const id = toInt(messageId, 0);
    if (!id) return;

    const reactions = normalizeReactionsArray(summary && summary.reactions);
    setReactionsByMessageId((prev) => ({
      ...(prev && typeof prev === 'object' ? prev : {}),
      [String(id)]: reactions,
    }));
  }, []);

  const loadReactionSummary = useCallback(
    async (messageId) => {
      const id = toInt(messageId, 0);
      if (!id) return null;
      const res = await comsApiGet(`/messages/${id}/reactions`);
      applyReactionSummary(id, res);
      return res;
    },
    [applyReactionSummary]
  );

  useEffect(() => {
    reactionUpdateHandlerRef.current = (ev) => {
      const id = toInt(ev && ev.message_id, 0);
      if (!id) return;
      // Only refresh reactions for messages currently loaded in this channel.
      if (!visibleMessageIds.some((x) => toInt(x, 0) === id)) return;
      loadReactionSummary(id).catch(() => {});
    };
  }, [visibleMessageIds, loadReactionSummary]);

  const markChannelReadNow = useCallback(
    async (forceMessageId = 0) => {
      if (!isLoggedIn || activeChannelKind !== 'text' || !isMessageChannel || !channelDbId) return;

      const targetMessageId = Math.max(toInt(forceMessageId, 0), toInt(latestLoadedMessageId, 0));
      if (!targetMessageId) return;
      if (markReadBusyRef.current) return;
      if (targetMessageId <= toInt(lastMarkedMessageIdRef.current, 0)) return;

      markReadBusyRef.current = true;
      try {
        const res = await comsApiPost(`/text-channels/${channelDbId}/read`, {});
        const lastSeenMessageId = Math.max(toInt(res && res.last_seen_message_id, 0), targetMessageId);
        lastMarkedMessageIdRef.current = lastSeenMessageId;

        setInitialReadState((prev) => ({
          ...(prev && typeof prev === 'object' ? prev : {}),
          user_id: myUserId,
          channel_id: channelDbId,
          last_seen_message_id: lastSeenMessageId,
          unread_count: 0,
          mention_count: 0,
          last_mention_message_id: 0,
        }));
      } catch {
      } finally {
        markReadBusyRef.current = false;
      }
    },
    [isLoggedIn, activeChannelKind, isMessageChannel, channelDbId, latestLoadedMessageId, myUserId]
  );

  async function handlePickFiles(fileList) {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;

    // Switching account during an upload asks first (see accounts/AccountSwitcher.js).
    const endActivity = beginActivity('upload', 'a file upload in progress');
    try {
      setUploading(true);
      setMessageActionErr('');
      const uploaded = await uploadFilesToComms(files);
      setPendingUploads((prev) => [...prev, ...uploaded]);
    } catch (e) {
      setMessageActionErr(String((e && e.message) || e));
    } finally {
      endActivity();
      setUploading(false);
      try {
        if (inputRef.current && typeof inputRef.current.focus === 'function') inputRef.current.focus();
      } catch {}
    }
  }

  function removePendingUpload(uploadId) {
    const id = toInt(uploadId, 0);
    if (!id) return;
    setPendingUploads((prev) => prev.filter((item) => toInt(item && item.id, 0) !== id));
  }

  function clearPendingUploads() {
    setPendingUploads([]);
  }

  function insertIntoDraftAtCursor(value) {
    const textToInsert = String(value || '');
    if (!textToInsert) return;

    const el = inputRef.current;
    if (!el) {
      setDraft((prev) => `${prev || ''}${textToInsert}`);
      return;
    }

    const start = typeof el.selectionStart === 'number' ? el.selectionStart : String(draft || '').length;
    const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : String(draft || '').length;
    const current = String(draft || '');
    const next = current.slice(0, start) + textToInsert + current.slice(end);
    setDraft(next);

    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + textToInsert.length;
        el.setSelectionRange(pos, pos);
      } catch {}
    });
  }

  function quoteMessage(msg) {
    const q = normalizeQuoteMessage(msg);
    setPendingQuote(q);
    try {
      const el = inputRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    } catch {}
  }

  function clearPendingQuote() {
    setPendingQuote(null);
    try {
      const el = inputRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    } catch {}
  }

  const quoteResetKeyRef = useRef(`${serverKey}|${channelUiId}|${activeChannelKind}`);
  useEffect(() => {
    setShowEmojiPicker(false);
    // The first run is the initial mount: keep a reply restored from this tab's memory.
    const resetKey = `${serverKey}|${channelUiId}|${activeChannelKind}`;
    if (quoteResetKeyRef.current !== resetKey) {
      quoteResetKeyRef.current = resetKey;
      quoteOwnerRef.current = memoryKey;
      const saved = readTabMemory(memoryUserId, memoryKey);
      setPendingQuote(saved && saved.quote ? saved.quote : null);
    }
    setPendingUploads([]);
    setReactionPickerMessage(null);
    setReactionsByMessageId({});
    setPinBusyMessageId('');
    setSuperPinBusyMessageId('');
    setWhatShallWePlayModalOpen(false);
    setWhatShallWePlayPayload(null);
    setWhatShallWePlayNotice('');
    // memoryKey/memoryUserId change together with the conversation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverKey, channelUiId, activeChannelKind]);

  useEffect(() => {
    const existingIds = new Set(
      safeArray(messagesRaw)
        .map((msg) => String(toInt(msg && msg.id, 0)))
        .filter(Boolean)
    );

    setReactionsByMessageId((prev) => {
      const next = {};
      let changed = false;

      Object.keys(prev && typeof prev === 'object' ? prev : {}).forEach((key) => {
        if (existingIds.has(key)) {
          next[key] = prev[key];
        } else {
          changed = true;
        }
      });

      const prevKeys = Object.keys(prev && typeof prev === 'object' ? prev : {});
      if (!changed && prevKeys.length === Object.keys(next).length) return prev;
      return next;
    });
  }, [messagesRaw]);

  useEffect(() => {
    const target = messagesContentRef.current;
    if (!target) return;
    if (typeof ResizeObserver === 'undefined') return;

    let rafA = 0;
    let rafB = 0;

    const ro = new ResizeObserver(() => {
      if (rafA) cancelAnimationFrame(rafA);
      if (rafB) cancelAnimationFrame(rafB);

      rafA = requestAnimationFrame(() => {
        if (shouldPinToBottomRef.current) {
          scrollToBottomHard(scrollerRef.current);
          rafB = requestAnimationFrame(() => {
            scrollToBottomHard(scrollerRef.current);
          });
          return;
        }

        maybeAutoScroll();
        rafB = requestAnimationFrame(() => {
          maybeAutoScroll();
        });
      });
    });

    try {
      ro.observe(target);
    } catch {}

    return () => {
      try {
        ro.disconnect();
      } catch {}
      if (rafA) cancelAnimationFrame(rafA);
      if (rafB) cancelAnimationFrame(rafB);
    };
  }, [maybeAutoScroll]);

  useEffect(() => {
    setTimeout(() => {
      if (shouldPinToBottomRef.current) {
        scrollToBottomHard(scrollerRef.current);
        return;
      }
      maybeAutoScroll();
    }, 0);
  }, [messagesWithGroupMeta.length, maybeAutoScroll]);

  const latestMessageReactionSignature = useMemo(() => {
    const latestId = toInt(latestLoadedMessageId, 0);
    if (!latestId) return '';

    const reactions = normalizeReactionsArray(reactionsByMessageId[String(latestId)]);
    const reactionKey = reactions
      .map((reaction) => {
        const names = safeArray(reaction.users)
          .map((u) => String(u && u.username ? u.username : '').trim())
          .filter(Boolean)
          .join('|');
        return `${reaction.emoji}:${reaction.count}:${reaction.reacted ? 1 : 0}:${names}`;
      })
      .join(',');

    return `${latestId}::${reactionKey}`;
  }, [latestLoadedMessageId, reactionsByMessageId]);

  useEffect(() => {
    if (!latestMessageReactionSignature) {
      lastBottomReactionSignatureRef.current = '';
      return;
    }

    const prev = String(lastBottomReactionSignatureRef.current || '');
    if (prev === latestMessageReactionSignature) return;

    const scrollerEl = scrollerRef.current;
    const wasNearBottom = shouldPinToBottomRef.current || isScrolledNearBottom(scrollerEl, 96);
    lastBottomReactionSignatureRef.current = latestMessageReactionSignature;
    if (!wasNearBottom) return;

    requestAnimationFrame(() => {
      scrollToBottomHard(scrollerRef.current);
      requestAnimationFrame(() => {
        scrollToBottomHard(scrollerRef.current);
      });
    });
  }, [latestMessageReactionSignature]);

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      const composed = composeOutgoingMessageBodyWithUploads(
        draft,
        pendingQuote,
        pendingUploads,
        buildQuoteBlockFromPending
      );
      const uploadIds = safeArray(pendingUploads)
        .map((item) => toInt(item && item.id, 0))
        .filter((v) => v > 0);

      if (!composed && !uploadIds.length) return;

      send({
        closeEmojiPicker: () => setShowEmojiPicker(false),
        textOverride: composed,
        uploadIds,
        font: selectedMessageFont,
      });

      setPendingQuote(null);
      setPendingUploads([]);
    }
  }

  const placeholder = useMemo(() => {
    if (!isLoggedIn) return 'Log in to chat';
    if (!isMessageChannel) return activeChannelKind === 'voice' ? 'Select a voice channel' : 'Select a text channel';
    if (!canSendHere) return 'You do not have permission to send messages in this channel';
    // Voice-channel chat is temporary (deleted on the server after the TTL); say so where people type.
    if (activeChannelKind === 'voice') return `Write a message · disappears after ${messageTtlMinutes || 15} minutes`;
    return 'Write a message';
  }, [isLoggedIn, isMessageChannel, activeChannelKind, canSendHere, messageTtlMinutes]);

  const voiceEmptyNotice = useMemo(() => {
    if (activeChannelKind !== 'voice') return null;
    if (loading || loadErr) return null;
    if (messagesWithGroupMeta.length > 0) return null;

    return (
      <div
        style={{
          marginBottom: 10,
          padding: isShellMobile ? '10px 12px' : '12px 14px',
          borderRadius: 12,
          border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
          background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
          color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.84 * var(--vx-ov, 1)))',
          fontSize: 13,
          lineHeight: 1.45,
        }}
      >
        This is the chat for the voice channel. Messages here disappear after {messageTtlMinutes || 15} minutes.
      </div>
    );
  }, [activeChannelKind, loading, loadErr, messagesWithGroupMeta.length, isShellMobile, messageTtlMinutes]);

  const unreadMentionMessageId = useMemo(() => {
    if (mentionBannerDismissed) return 0;
    if (activeChannelKind !== 'text') return 0;
    if (!isMessageChannel) return 0;
    if (!mentionSnapshotReady) return 0;
    if (!initialReadState) return 0;
    if (!safeArray(messagesRaw).length) return 0;
    return findUnreadMentionMessageId(messagesRaw, myUserId, myMentionNames, initialReadState);
  }, [
    mentionBannerDismissed,
    activeChannelKind,
    isMessageChannel,
    mentionSnapshotReady,
    initialReadState,
    messagesRaw,
    myUserId,
    myMentionNames,
  ]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (activeChannelKind !== 'text') return;
    if (!isMessageChannel) return;
    if (!mentionSnapshotReady) return;
    if (!initialReadState) return;
    if (!safeArray(messagesRaw).length) return;
    if (unreadMentionMessageId > 0) return;

    const state = initialReadState && typeof initialReadState === 'object' ? initialReadState : {};
    const lastSeenMessageId = toInt(state.last_seen_message_id, 0);
    const latestMessageId = toInt(latestLoadedMessageId, 0);
    if (!latestMessageId || latestMessageId <= lastSeenMessageId) return;

    const t = setTimeout(() => {
      markChannelReadNow(latestMessageId).catch(() => {});
    }, 250);

    return () => clearTimeout(t);
  }, [
    isLoggedIn,
    activeChannelKind,
    isMessageChannel,
    mentionSnapshotReady,
    initialReadState,
    messagesRaw,
    unreadMentionMessageId,
    latestLoadedMessageId,
    markChannelReadNow,
  ]);

  const mentionBannerVisible = unreadMentionMessageId > 0;

  const scrollMessageIntoView = useCallback(
    (messageId, opts = {}) => {
      const id = toInt(messageId, 0);
      const markRead = !!(opts && opts.markRead);
      if (!id) return false;

      const el = tryFindMessageElement(id);
      if (!el) return false;

      try {
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      } catch {
        try {
          el.scrollIntoView();
        } catch {}
      }

      flashMessageElement(el);

      if (markRead) {
        setTimeout(() => {
          markChannelReadNow(id).catch(() => {});
        }, 150);
      }

      return true;
    },
    [markChannelReadNow]
  );

  const fetchMessageContextUntilFound = useCallback(
    async (messageId) => {
      const targetId = toInt(messageId, 0);
      if (!targetId) return false;
      if (activeChannelKind !== 'text' || !isMessageChannel || !channelDbId) return false;

      let combined = safeArray(messagesRaw);
      if (combined.some((msg) => toInt(msg && msg.id, 0) === targetId)) return true;

      let beforeId = combined.reduce((min, msg) => {
        const id = toInt(msg && msg.id, 0);
        if (!id) return min;
        if (!min) return id;
        return id < min ? id : min;
      }, 0);

      let pageCount = 0;
      const maxPages = 25;
      const pageSize = 200;

      while (pageCount < maxPages) {
        const qs = beforeId > 0 ? `?limit=${pageSize}&before_id=${beforeId}` : `?limit=${pageSize}`;
        const res = await comsApiGet(
          `/text-channels/${encodeURIComponent(String(channelDbId))}/messages${qs}`
        ).catch(() => null);
        const rows = safeArray(res && res.messages);
        if (!rows.length) break;

        combined = mergeMessagesById(combined, rows);
        if (rows.some((msg) => toInt(msg && msg.id, 0) === targetId)) {
          setMessagesRaw(combined);
          return true;
        }

        const oldest = rows.reduce((min, msg) => {
          const id = toInt(msg && msg.id, 0);
          if (!id) return min;
          if (!min) return id;
          return id < min ? id : min;
        }, 0);

        pageCount += 1;
        if (!oldest || (beforeId > 0 && oldest >= beforeId) || rows.length < pageSize) break;
        beforeId = oldest;
      }

      setMessagesRaw(combined);
      return combined.some((msg) => toInt(msg && msg.id, 0) === targetId);
    },
    [activeChannelKind, isMessageChannel, channelDbId, messagesRaw, setMessagesRaw]
  );

  const jumpToSpecificMessage = useCallback(
    async (messageId, opts = {}) => {
      const id = toInt(messageId, 0);
      const allowLoadOlder = !opts || opts.loadOlder !== false;
      const markRead = !!(opts && opts.markRead);
      if (!id) return;

      try {
        if (stickToBottomRef && stickToBottomRef.current != null) stickToBottomRef.current = false;
      } catch {}

      try {
        if (typeof setHideScrollbar === 'function') setHideScrollbar(false);
      } catch {}

      if (scrollMessageIntoView(id, { markRead })) return;
      if (allowLoadOlder) {
        try {
          await fetchMessageContextUntilFound(id);
        } catch {}
      }

      const tryJump = (attempt = 0) => {
        if (scrollMessageIntoView(id, { markRead })) return;
        if (attempt >= 24) return;
        setTimeout(() => tryJump(attempt + 1), 80);
      };

      requestAnimationFrame(() => tryJump(0));
    },
    [fetchMessageContextUntilFound, scrollMessageIntoView, setHideScrollbar, stickToBottomRef]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    function onJumpToPinnedMessage(e) {
      const detail = e && e.detail && typeof e.detail === 'object' ? e.detail : {};
      const targetMessageId = toInt(detail.messageId, 0);
      const targetChannelId = toInt(detail.channelId, 0);
      const targetServerId = String(detail.serverId || '').trim();
      const currentServerId = String(server && server.id != null ? server.id : '').trim();

      if (!targetMessageId || !targetChannelId) return;
      if (targetChannelId !== toInt(channelDbId, 0)) return;
      if (targetServerId && currentServerId && targetServerId !== currentServerId) return;

      jumpToSpecificMessage(targetMessageId, { loadOlder: true, markRead: false });
    }

    window.addEventListener('voxemera-jump-to-message', onJumpToPinnedMessage);
    return () => {
      window.removeEventListener('voxemera-jump-to-message', onJumpToPinnedMessage);
    };
  }, [server, channelDbId, jumpToSpecificMessage]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    function onOpenWhatShallWePlay(e) {
      const detail = e && e.detail && typeof e.detail === 'object' ? e.detail : null;
      if (!detail) return;

      const targetServerId = String(detail.serverId || '').trim();
      const currentServerId = String(server && server.id != null ? server.id : '').trim();
      const targetChannelDbId = toInt(detail.channelDbId, 0);
      const currentChannelDbId = toInt(channelDbId, 0);
      const targetChannelId = String(detail.channelId || '').trim();
      const currentChannelId = String(channelUiId || '').trim();

      if (targetServerId && currentServerId && targetServerId !== currentServerId) return;
      if (targetChannelDbId > 0 && currentChannelDbId > 0 && targetChannelDbId !== currentChannelDbId) return;
      if (!targetChannelDbId && targetChannelId && currentChannelId && targetChannelId !== currentChannelId) return;

      openWhatShallWePlayModal(detail);
    }

    window.addEventListener('vox-what-shall-we-play-open', onOpenWhatShallWePlay);
    return () => {
      window.removeEventListener('vox-what-shall-we-play-open', onOpenWhatShallWePlay);
    };
  }, [server, channelDbId, channelUiId, openWhatShallWePlayModal]);

  function jumpToUnreadMention() {
    const messageId = toInt(unreadMentionMessageId, 0);
    if (!messageId || mentionJumpBusy) return;

    setMentionJumpBusy(true);
    setMentionBannerDismissed(true);

    jumpToSpecificMessage(messageId, { loadOlder: true, markRead: true })
      .catch(() => {})
      .finally(() => {
        setMentionJumpBusy(false);
      });
  }

  const avatarWrapperStyle = {
    width: 32,
    height: 32,
    borderRadius: '50%',
    overflow: 'hidden',
    background: 'var(--vxs-1, #111214)',
    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.15 * var(--vx-ov, 1)))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: 16,
    color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.8 * var(--vx-ov, 1)))',
  };

  const mobileLeftInnerPad = 8;
  const mobileRightInnerPad = 6;

  const chatRootStyle = isShellMobile
    ? {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }
    : {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
      };

  const messagesStyle = {
    paddingBottom: 0,
    ...(isShellMobile
      ? {
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          width: '100%',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          paddingLeft: mobileLeftInnerPad,
          paddingRight: mobileRightInnerPad,
          paddingTop: 8,
          paddingBottom: 6,
          boxSizing: 'border-box',
          overflowX: 'hidden',
        }
      : {
          flex: 1,
          minHeight: 0,
        }),
  };

  const inputBarStyle = {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    ...(isShellMobile
      ? {
          paddingLeft: mobileLeftInnerPad,
          paddingRight: mobileRightInnerPad,
          paddingTop: 8,
          paddingBottom: 'max(8px, env(safe-area-inset-bottom, 0px))',
          borderTop: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
          background: 'rgba(var(--vxs-2-rgb, 18,22,32), 0.96)',
          backdropFilter: 'blur(8px)',
          position: 'sticky',
          bottom: 0,
          zIndex: 5,
          boxSizing: 'border-box',
        }
      : {}),
  };

  const mobileComposerCompact = isShellMobile;

  const reactToMessage = useCallback(
    (rawMsg) => {
      const msg = rawMsg && typeof rawMsg === 'object' ? rawMsg : {};
      const messageId = toInt(msg.id, 0);
      if (!messageId) return;
      setMessageActionErr('');
      setShowEmojiPicker(false);
      setReactionPickerMessage(msg);
    },
    [setMessageActionErr]
  );

  async function submitReactionEmoji(emojiNative) {
    const messageId = toInt(reactionPickerMessage && reactionPickerMessage.id, 0);
    const emoji = String(emojiNative || '').trim();
    if (!messageId || !emoji) return;

    try {
      setMessageActionErr('');
      const res = await comsApiPost(`/messages/${messageId}/reactions`, { emoji });
      applyReactionSummary(messageId, res);
      setReactionPickerMessage(null);
    } catch (e) {
      try {
        await loadReactionSummary(messageId);
      } catch {}
      setMessageActionErr(String((e && e.message) || e));
    }
  }

  const onClickSend = () => {
    const composed = composeOutgoingMessageBodyWithUploads(
      draft,
      pendingQuote,
      pendingUploads,
      buildQuoteBlockFromPending
    );
    const uploadIds = safeArray(pendingUploads)
      .map((item) => toInt(item && item.id, 0))
      .filter((v) => v > 0);

    if (!composed && !uploadIds.length) return;

    send({
      closeEmojiPicker: () => setShowEmojiPicker(false),
      textOverride: composed,
      uploadIds,
      font: selectedMessageFont,
    });

    setPendingQuote(null);
    setPendingUploads([]);
  };

  // Archived channels are read-only (the server refuses writes too); auto-archive warning near the end.
  const { state: channelLifecycle, reload: reloadChannelLifecycle } = useChannelState(activeChannelKind === 'text' && isMessageChannel ? channelDbId : 0);
  const channelArchived = !!(channelLifecycle && channelLifecycle.archived);

  // Search results and bookmarks open a message: scroll to it, loading older history when needed.
  const channelPageReady = useMemo(
    () => !!channelDbId && messagesRaw.some((m) => Number(m && m.channel_id) === Number(channelDbId)),
    [channelDbId, messagesRaw]
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let timer = null;
    const tryPending = () => {
      const p = window.__voxPendingJump;
      // Wait for the channel's first page: its initial scroll-to-bottom must not undo the jump.
      if (!p || !channelDbId || Number(p.channelId) !== Number(channelDbId) || loading || !channelPageReady) return;
      if (Date.now() - (p.at || 0) > 60000) {
        window.__voxPendingJump = null;
        return;
      }
      window.__voxPendingJump = null;
      timer = setTimeout(() => {
        if (typeof releaseBottomHold === 'function') releaseBottomHold();
        shouldPinToBottomRef.current = false;
        jumpToSpecificMessage(Number(p.messageId), { loadOlder: true, markRead: false });
      }, 250);
    };
    tryPending();
    window.addEventListener('vox-pending-jump', tryPending);
    return () => {
      window.removeEventListener('vox-pending-jump', tryPending);
      if (timer) clearTimeout(timer);
    };
  }, [channelDbId, loading, channelPageReady, jumpToSpecificMessage, releaseBottomHold]);

  // Channel Interactions: where "Make poll / task" (message actions) creates them. New interactions start from
  // the channel menu (right-click, or long-press on touch), not from the composer.
  const interactionTarget = useMemo(
    () => (canType && permChannelId && serverKey ? { serverKey, kind: activeChannelKind, channelId: permChannelId, channelName: String((channel && channel.name) || '') } : null),
    [canType, permChannelId, serverKey, activeChannelKind, channel]
  );
  const convertMessageToInteraction = useCallback(
    (raw, m) => {
      if (!interactionTarget || !raw) return;
      const text = String(raw.body || '').replace(/\s+/g, ' ').trim();
      openInteractionCreator({
        ...interactionTarget,
        type: 'poll',
        prefill: {
          title: text.slice(0, 200),
          taskLines: text.slice(0, 300),
          source_message_id: Number(raw.id),
          taskSourceMessageId: Number(raw.id),
          sourceAuthor: (m && (m.author || m.username)) || raw.display_name || raw.username || '',
        },
      });
    },
    [interactionTarget]
  );

  const messageActions = useMemo(() => {
    return buildSharedMessageActions({
      convertMessage: interactionTarget ? convertMessageToInteraction : null,
      bookmarkMessage: activeChannelKind === 'text' ? (raw) => saveBookmark('message', raw.id) : null,
      readOnly: channelArchived,
      canAddReactions: chatPerms.has('add_reactions'),
      // Encrypted messages are reported by disclosing selected ones from this device (e2ee/EncryptedReportDialog.js).
      reportMessage: (raw) => (raw && raw.encryption === 'e2ee' ? setE2eeReportId(raw.id) : setReportTarget(raw)),
      myUserId,
      quoteMessage,
      reactToMessage,
      beginEditMessage,
      deleteMessage,
      togglePinnedMessage,
      toggleSuperPinnedMessage,
      canEditMessage,
      canDeleteMessage,
      canPinMessage,
      canSuperPinMessage,
      editBusyMessageId,
      deleteBusyMessageId,
      pinBusyMessageId,
      superPinBusyMessageId,
    });
  }, [
    myUserId,
    quoteMessage,
    reactToMessage,
    beginEditMessage,
    deleteMessage,
    togglePinnedMessage,
    toggleSuperPinnedMessage,
    canEditMessage,
    canDeleteMessage,
    canPinMessage,
    canSuperPinMessage,
    editBusyMessageId,
    deleteBusyMessageId,
    pinBusyMessageId,
    superPinBusyMessageId,
    chatPerms,
    activeChannelKind,
    channelArchived,
    interactionTarget,
    convertMessageToInteraction,
  ]);

  return (
    <div className="coms-view" style={chatRootStyle} data-coms-chat-mobile={isShellMobile ? '1' : '0'}>
      <ChatViewScrollbarStyle />
      {isMessageChannel ? <ChannelEncryptionBar channelKind={activeChannelKind} e2ee={channelE2ee} /> : null}

      {reactionPickerMessage ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 5000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 12,
            background: 'rgba(0,0,0,0.35)',
            backdropFilter: 'blur(2px)',
          }}
        >
          <div
            ref={reactionPickerRef}
            style={{
              borderRadius: 14,
              overflow: 'hidden',
              border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
              boxShadow: '0 14px 40px rgba(0,0,0,0.45)',
              maxWidth: 'calc(100vw - 24px)',
            }}
          >
            <Picker
              data={data}
              theme={themeBase === 'light' ? 'light' : 'dark'}
              previewPosition="none"
              searchPosition="sticky"
              navPosition="top"
              skinTonePosition="search"
              perLine={9}
              maxFrequentRows={2}
              emojiSize={30}
              emojiButtonSize={42}
              onEmojiSelect={(emoji) => {
                const ch = emoji && emoji.native ? String(emoji.native) : '';
                if (!ch) return;
                submitReactionEmoji(ch).catch(() => {});
              }}
            />
          </div>
        </div>
      ) : null}

      {isShellMobile && isMessageChannel && channelKind === 'text' && channel && String(channel.topic || '').trim() ? (
        // Phones have no channel header: the topic sits at the top of the chat instead (desktop: TopTabs).
        <div
          className="vx-chat-topic"
          data-testid="chat-topic"
          title={String(channel.topic)}
          style={{
            flexShrink: 0,
            padding: '6px 12px',
            fontSize: 12,
            lineHeight: 1.35,
            color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.72 * var(--vx-ov, 1)))',
            borderBottom: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          <b># {channel.name}</b> · {String(channel.topic).trim()}
        </div>
      ) : null}

      <div
        className={'coms-messages' + (hideScrollbar ? ' coms-messages--hide-scrollbar' : '')}
        ref={scrollerRef}
        style={messagesStyle}
      >
        {voiceEmptyNotice}

        {whatShallWePlayNotice ? (
          <div
            style={{
              marginBottom: 10,
              padding: isShellMobile ? '10px 12px' : '12px 14px',
              borderRadius: 12,
              border: '1px solid rgba(130,255,170,0.22)',
              background: 'rgba(130,255,170,0.10)',
              color: '#d9ffe6',
              fontSize: 13,
              lineHeight: 1.35,
              fontWeight: 700,
            }}
          >
            {whatShallWePlayNotice}
          </div>
        ) : null}

        <ChatMessageList
          messagesWithGroupMeta={messagesWithGroupMeta}
          messages={messages}
          filteredMessages={filteredMessages}
          loading={loading}
          loadErr={loadErr}
          membersLoaded={membersLoaded}
          messageActionErr={messageActionErr}
          messageSearchNorm={messageSearchNorm}
          messageSearch={messageSearch}
          isShellMobile={isShellMobile}
          avatarWrapperStyle={avatarWrapperStyle}
          messagesContentRef={messagesContentRef}
          editingMessageId={editingMessageId}
          editDraft={editDraft}
          editBusyMessageId={editBusyMessageId}
          deleteBusyMessageId={deleteBusyMessageId}
          hoveredMessageId={hoveredMessageId}
          setHoveredMessageId={setHoveredMessageId}
          setEditDraft={setEditDraft}
          beginEditMessage={beginEditMessage}
          deleteMessage={deleteMessage}
          cancelEditMessage={cancelEditMessage}
          saveEditMessage={saveEditMessage}
          quoteMessage={quoteMessage}
          messageActions={messageActions}
          suppressEmptyState={activeChannelKind === 'voice'}
        />
      </div>

      {mentionBannerVisible ? (
        <div
          style={{
            paddingLeft: isShellMobile ? mobileLeftInnerPad : 12,
            paddingRight: isShellMobile ? mobileRightInnerPad : 12,
            paddingTop: 6,
            paddingBottom: 6,
            borderTop: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
            background: isShellMobile ? 'rgba(var(--vxs-2-rgb, 18,22,32), 0.96)' : 'transparent',
          }}
        >
          <button
            type="button"
            onClick={jumpToUnreadMention}
            disabled={mentionJumpBusy}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              padding: isShellMobile ? '10px 12px' : '9px 12px',
              borderRadius: 12,
              border: '1px solid rgba(var(--vx-link-rgb, 110,160,255), 0.26)',
              background: 'rgba(var(--vx-link-rgb, 110,160,255), 0.12)',
              color: 'var(--vx-link, #a9c4ff)',
              fontSize: 13,
              fontWeight: 600,
              cursor: mentionJumpBusy ? 'default' : 'pointer',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'var(--vx-link, #8db4ff)',
                  flexShrink: 0,
                  boxShadow: '0 0 0 4px rgba(var(--vx-link-rgb, 110,160,255), 0.16)',
                }}
              />
              <span style={{ minWidth: 0, textAlign: 'left' }}>You were mentioned in unread messages</span>
            </span>
            <span style={{ color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.82 * var(--vx-ov, 1)))', fontWeight: 700, flexShrink: 0 }}>
              {mentionJumpBusy ? '...' : 'Tap here'}
            </span>
          </button>
        </div>
      ) : null}

      <ReportDialog message={reportTarget} onClose={() => setReportTarget(null)} />
      {e2eeReportId != null ? (
        <EncryptedReportDialog
          scope="channel"
          conversationId={channelDbId}
          messages={safeArray(messagesRaw)
            .filter((m) => m && m.encryption === 'e2ee' && !m.system_kind)
            .map((m) => {
              const r = decryptedResult(`ch:${m.id}`);
              return {
                id: m.id,
                author: m.display_name || m.username || `User ${m.user_id}`,
                text: r && r.ok ? r.body : '',
                ok: !!(r && r.ok),
                ciphertext: m.ciphertext,
                senderDevice: r ? r.senderDevice : '',
                verified: !!(r && r.verified),
                fromBackup: !!(r && r.fromBackup),
                unsignedDevice: !!(r && r.unsignedDevice),
                mine: String(m.user_id) === String(myUserId),
              };
            })}
          initialId={e2eeReportId}
          onClose={() => setE2eeReportId(null)}
        />
      ) : null}

      <ChatLiveStatus
        typingState={typingState}
        streamStatus={streamStatus}
        sendErr={sendErr}
        onDismissSendErr={() => setSendErr('')}
        isShellMobile={isShellMobile}
      />

      <ArchivedChannelBar state={channelLifecycle} serverKey={serverKey} channelDbId={channelDbId} onRestored={reloadChannelLifecycle} />

      {channelArchived ? null : (
      <ChatComposer
        isShellMobile={isShellMobile}
        canType={canType && !channelE2ee.blocked}
        showEmojiPicker={showEmojiPicker}
        setShowEmojiPicker={setShowEmojiPicker}
        emojiButtonRef={emojiButtonRef}
        emojiPopoverRef={emojiPopoverRef}
        inputRef={inputRef}
        draft={draft}
        setDraft={setDraft}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        mobileComposerCompact={mobileComposerCompact}
        sending={sending}
        send={onClickSend}
        insertIntoDraftAtCursor={insertIntoDraftAtCursor}
        inputBarStyle={inputBarStyle}
        pendingQuote={pendingQuote}
        clearPendingQuote={clearPendingQuote}
        pendingUploads={pendingUploads}
        removePendingUpload={removePendingUpload}
        onPickFiles={channelE2ee.active ? undefined : handlePickFiles}
        uploading={uploading}
        clearPendingUploads={clearPendingUploads}
        mentionMembers={mentionMembers}
        fontOptions={MESSAGE_FONT_OPTIONS}
        selectedFont={selectedMessageFont}
        onChangeFont={setSelectedMessageFont}
        serverKey={serverKey}
        canAttach={chatPerms.has('attach_files') && !channelE2ee.active}
        canSendStickers={(chatPerms.has('send_stickers') || chatPerms.has('use_external_stickers')) && !channelE2ee.active}
        canSendServerStickers={chatPerms.has('send_stickers')}
        canSendExternalStickers={chatPerms.has('use_external_stickers')}
        allowEveryoneMention={chatPerms.has('mention_everyone')}
        onSendSticker={(token) => send({ textOverride: token, keepDraft: true })}
        onOpenStickerManager={() => {
          try {
            window.__voxPendingSettingsTab = 'Stickers';
            window.dispatchEvent(new CustomEvent('vox-open-settings', { detail: { tab: 'Stickers' } }));
          } catch {}
        }}
      />
      )}

      <WhatShallWePlayModal
        open={whatShallWePlayModalOpen}
        payload={whatShallWePlayPayload}
        onClose={closeWhatShallWePlayModal}
        onSubmitted={handleWhatShallWePlaySubmitted}
      />
    </div>
  );
}